import { useTageaForm } from '@web-libs/core/forms/application/use-tagea-form.concern';
import { MutableRefObject, useEffect, useMemo, useRef, useState } from 'react';
import { validateEmail } from '@web-libs/core/forms/application/validators/email-validator';
import { useAuth } from '@web-libs/features/auth/main/application/use-auth.concern';
import { isValidDate, isValidPhoneNumber } from '@web-libs/util/dates/date';
import { RequestError } from '@web-libs/core/http/api/http.errors';
import {
  EmailInUseError,
  UserNameInUseError,
} from '@web-libs/features/auth/main/domain/auth.errors';

import { useDictionaryContext } from '@web-libs/core/internationalization/dictionary.context';
import { validateUsername } from '@web-libs/core/forms/application/validators/username-validator';
import { lengthValidator } from '@web-libs/core/forms/application/validators/length-validator';
import { useInstitutionContext } from '@web-libs/features/institution/application/use-institution-context';
import { useSpecialization } from '@web-libs/features/specializations/application/specialization.concern';
import { Institution } from '@web-libs/features/institution/domain/institution.types';
import { useRepositories } from '@web-libs/core/repositories/application/repository-concerns';

export type RegisterFormInputs = {
  anonymous: boolean;
  firstName?: string;
  lastName?: string;
  email?: string;
  birth?: string;
  mobile?: string;
  userName: string;
  password: string;
  passwordRepeat?: string; // Optional since it's handled by EnhancedPasswordField
  privacyPolicy?: boolean;
  specializationId?: string;
};

export const useRegisterForm = (onSuccess: () => void) => {
  const { getRepo } = useRepositories();

  const instId = useInstitutionContext();
  const { specializations, fetchSpecializations } = useSpecialization();
  const [inst, setInst] = useState<Institution>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchSpecializations(instId).then((specs) => {
      if (specs.length === 0) {
        unregister('specializationId');
      }
    });
    const instRepo = getRepo('InstitutionRepository');
    instRepo.getInstitutionById(instId).then((institution) => {
      setInst(institution);
    });
  }, []);

  const {
    register,
    handleSubmit,
    formState,
    registerServerErrorMapper,
    serverError,
    clearErrors,
    setError,
    disableSubmit,
    watch,
    unregister,
    trigger,
  } = useTageaForm<RegisterFormInputs>();
  const authConcern = useAuth();
  const watchAnonymous = watch('anonymous', false);
  const dict = useDictionaryContext();
  const dictValidation = dict.validator;
  const typingTimer: MutableRefObject<NodeJS.Timeout | undefined> = useRef();
  const checkEmailUsed = async (value?: string): Promise<string | boolean> => {
    clearTimeout(typingTimer.current);
    return new Promise<string | boolean>((resolve) => {
      typingTimer.current = setTimeout(async () => {
        _checkEmailUsed(resolve, value);
      }, 500);
    }).then((value) => {
      if (typeof value == 'string') setError('userName', { message: value });
      else clearErrors('userName');
      return value;
    });
  };

  const _checkEmailUsed = (
    resolve: (value: string | boolean | PromiseLike<string | boolean>) => void,
    value?: string,
  ) => {
    if (!value) return resolve(dict.validator.mapErrorMessages.required);
    const validEmail = validateEmail(value, dict);
    if (typeof validEmail == 'string') return resolve(validEmail);
    authConcern
      .checkEmailUsed(value)
      .then((emailUsed) => {
        emailUsed ? resolve(dictValidation.emailInUse) : resolve(true);
      })
      .catch(() => resolve(true));
  };

  function _checkUsernameUsed(
    value: string,
    resolve: (value: string | boolean) => void,
  ) {
    if (!value) return resolve(dict.validator.mapErrorMessages.required);
    const validLength = lengthValidator(value, dict, 5, 25);
    if (typeof validLength == 'string') return resolve(validLength);
    const validUsername = validateUsername(value, dict);
    if (typeof validUsername == 'string') return resolve(validUsername);
    authConcern
      .checkUsernameUsed(value)
      .then((userNameUsed) => {
        userNameUsed ? resolve(dictValidation.usernameInUse) : resolve(true);
      })
      .catch(() => resolve(true));
  }

  const checkUsernameUsed = async (
    value: string,
  ): Promise<string | boolean> => {
    clearTimeout(typingTimer.current);
    return new Promise<string | boolean>((resolve) => {
      typingTimer.current = setTimeout(async () => {
        _checkUsernameUsed(value, resolve);
      }, 500);
    }).then((value) => {
      if (typeof value == 'string') setError('userName', { message: value });
      else clearErrors('userName');
      return value;
    });
  };

  useEffect(() => {
    if (watchAnonymous) {
      clearErrors('userName');
      unregister('firstName');
      unregister('lastName');
      unregister('email');
      unregister('birth');
    } else {
      clearErrors('email');
      unregister('userName');
    }
  }, [watchAnonymous]);

  // Password repeat validation is now handled by EnhancedPasswordField component

  const fields = useMemo(() => {
    const showSpecialization =
      !!inst?.enableSpecializationRegistration && specializations.length > 0;

    if (watchAnonymous) {
      return {
        userName: register('userName', dict.registration.username, {
          validate: checkUsernameUsed,
        }),
        password: register('password', dict.registration.password, {
          required: dict.validator.passwordRequired,
        }),
        // passwordRepeat is handled internally by EnhancedPasswordField component
        anonymous: register('anonymous', dict.registration.anonymCheckbox),
        specializationId: !showSpecialization
          ? undefined
          : register(
              'specializationId',
              dict.registration.specialization_label,
              { required: dict.validator.mapErrorMessages.required },
            ),
        privacyPolicy: register('privacyPolicy', undefined, {
          required: 'Bitte akzeptieren Sie die Datenschutzbestimmungen',
        }),
      };
    } else {
      return {
        firstName: register('firstName', dict.registration.firstName, {
          required: dict.validator.firstNameRequired,
        }),
        lastName: register('lastName', dict.registration.lastName, {
          required: dict.validator.lastNameRequired,
        }),
        email: register('email', dict.registration.email, {
          validate: checkEmailUsed,
        }),
        birth: register('birth', dict.registration.birth, {
          required: dict.validator.birthRequired,
          validate: {
            validDate: (value) => {
              if (!value) {
                return true;
              } else {
                return isValidDate(value) || dict.validator.wrongBirth;
              }
            },
          },
        }),
        mobile: register('mobile', dict.registration.mobile, {
          validate: {
            validDate: (value) => {
              if (!value) {
                return true;
              } else {
                return (
                  isValidPhoneNumber(value) || dict.validator.wrongPhoneNumber
                );
              }
            },
          },
        }),
        password: register('password', dict.registration.password, {
          required: dict.validator.passwordRequired,
        }),
        // passwordRepeat is handled internally by EnhancedPasswordField component
        anonymous: register('anonymous', dict.registration.anonymCheckbox),
        specializationId: !showSpecialization
          ? undefined
          : register(
              'specializationId',
              dict.registration.specialization_label,
              { required: dict.validator.mapErrorMessages.required },
            ),
        privacyPolicy: register('privacyPolicy', undefined, {
          required: 'Bitte akzeptieren Sie die Datenschutzbestimmungen',
        }),
      };
    }
  }, [watchAnonymous, formState, specializations, inst]);

  useEffect(() => {
    registerServerErrorMapper((error: any) => {
      if (error instanceof RequestError) {
        const errorObject = JSON.parse(error.rawMessage);
        return errorObject.message;
      }
      if (
        error instanceof UserNameInUseError ||
        error instanceof EmailInUseError
      ) {
        return error.message;
      }
      return dict.error.registrationError;
    });
  }, []);

  const onSubmit = async (data: RegisterFormInputs) => {
    setIsLoading(true);
    try {
      const success = await authConcern.register({ ...data });
      if (success) {
        // Keep loading state on during redirect
        onSuccess();
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const _handleSubmit = handleSubmit(onSubmit);

  return {
    fields,
    handleSubmit: _handleSubmit,
    serverError,
    trigger,
    specializations,
    isSubmitting: formState.isSubmitting || isLoading,
  };
};
